import { useAtom, atom } from 'jotai';
import randomString from '@utils/randomString';
import { useCallback } from 'react';
import { Modal, ModalEventHandlers, ModalType } from '@models/modals/Modals';
import useSamLineSourceModalEventHandlers from '@components/modals/samLineSourceModal/hooks/useSamLineSourceModalEventHandlers';
import useWorkflowActionValidationModalEventHandlers from '@components/modals/workflowActionValidationModal/hooks/useWorkflowActionValidationModalEventHandlers';
import useDynamicAssignModalEventHandlers from '@components/modals/dynamicAssignModal/hooks/useDynamicAssignModalEventHandlers';
import usePerformDocumentActionModalEventHandlers from '@components/modals/performDocumentActionModal/hooks/usePerformDocumentActionModalEventHandlers';

const modalsAtom = atom<Array<Modal>>([]);

const useModals = () => {
    const [modals, setModals] = useAtom(modalsAtom);

    const modalEventHandlers: ModalEventHandlers = {
        [ModalType.DynamicAssign]: useDynamicAssignModalEventHandlers(),
        [ModalType.PerformDocumentAction]: usePerformDocumentActionModalEventHandlers(),
        [ModalType.SamLineSource]: useSamLineSourceModalEventHandlers(),
        [ModalType.WorkflowActionValidation]: useWorkflowActionValidationModalEventHandlers(),
    };

    const closeModal = useCallback(
        (id: string) => {
            setModals((oldModals) => {
                const modalToClose = oldModals.find((modal) => modal.id === id);
                if (modalToClose && modalEventHandlers[modalToClose.type]?.onClose) {
                    // @ts-expect-error TS2345
                    modalEventHandlers[modalToClose.type]?.onClose?.(modalToClose.modalData);
                }

                return oldModals.filter((modal) => modal.id !== id);
            });
        },
        [setModals, modals],
    );

    const updateModal = useCallback(
        <T extends ModalType>(id: string, newState: Partial<Omit<Modal<T>, 'id' | 'type' | 'closeModal' | 'hideModal' | 'updateModal'>>) => {
            setModals((oldModals) =>
                oldModals.map((modal) => {
                    if (modal.id !== id) return modal;

                    return {
                        ...modal,
                        ...newState,
                        modalData: newState.modalData
                            ? {
                                  ...modal.modalData,
                                  ...newState.modalData,
                              }
                            : modal.modalData,
                    };
                }),
            );
        },
        [setModals],
    );

    const openModal = useCallback(
        <T extends ModalType>(modal: Omit<Modal<T>, 'id' | 'closeModal' | 'updateModal'>) => {
            const id = randomString();

            const modalWithHandlers: Modal<T> = {
                ...modal,
                id,
                closeModal: () => closeModal(id),
                updateModal: (state) => updateModal(id, state),
            };

            if (modalEventHandlers[modal.type]?.onOpen) {
                modalEventHandlers[modal.type]?.onOpen?.(modal.modalData);
            }

            setModals((oldModals) => [...oldModals, modalWithHandlers]);
        },
        [closeModal, updateModal],
    );

    const getModalByType = useCallback(
        <T extends ModalType>(type: T): Modal<T> | undefined => {
            return modals.find((modal) => modal.type === type) as Modal<T> | undefined;
        },
        [modals],
    );

    return {
        modals,
        openModal,
        closeModal,
        getModalByType,
        updateModal,
    };
};

export default useModals;
